/* OsPlanServerImagesRenderer.js */

"use strict";
const _ = require("lodash");
const TreeComponent = require("@gluenetworks/tree_view").Tree;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const MultipleSelectListControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Multiple.SelectList;

class OsPlanServerImagesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-plan-server-images-gl");
        this._formFieldset = document.createElement("fieldset");
        this._imagesContainer = document.createElement("div");
        this._legend = document.createElement("legend");
        this._legend.innerHTML = "Server";
        this._treeComponent = undefined;
        this._selSelectBox = undefined;
        this._selectedImagesSection = new MultipleSelectListControl({
            options: [
                {
                    label: "",
                    disabled: true,
                    value: { id: "", name: "" }
                }
            ]
        });

        this._addButtonIcon = document.createElement("base-icon-gl");
        this._addButtonIcon.setAttribute("class", "btn-icon-chevron-right");
        this._addButtonIcon.dataset.icon = "common.chevron";

        this._removeButtonIcon = document.createElement("base-icon-gl");
        this._removeButtonIcon.setAttribute("class", "btn-icon-chevron-left");
        this._removeButtonIcon.dataset.icon = "common.chevron";

        this._removeAllButtonIcon = document.createElement("base-icon-gl");
        this._removeAllButtonIcon.setAttribute("class", "btn-icon-left");
        this._removeAllButtonIcon.dataset.icon = "common.back";

        this._addButton = new ButtonControl({});
        this._addButton._renderer._rootElem.appendChild(this._addButtonIcon);

        this._removeButton = new ButtonControl({});
        this._removeButton._renderer._rootElem.prepend(this._removeButtonIcon);

        this._removeAllButton = new ButtonControl({});
        this._removeAllButton._renderer._rootElem.prepend(this._removeAllButtonIcon);

        /* Sort container */
        this._sortContainer = document.createElement("div");
        this._sortContainer.classList.add("sort-container");
        this._sortContainer.setAttribute("disabled", "disabled");

        this._sortUp = document.createElement("div");
        this._sortUp.classList.add("pick-list-control", "sort-control");
        this._sortUp.innerHTML = "&#8593";

        this._sortDown = document.createElement("div");
        this._sortDown.classList.add("pick-list-control", "sort-control");
        this._sortDown.innerHTML = "&#8595";

        this._sortContainer.appendChild(this._sortUp);
        this._sortContainer.appendChild(this._sortDown);
        /* Sort container */
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._treeComponent.on("UserSelectedPath", (this._onTreeUserOpenPath = this._onTreeUserOpenPath.bind(this)));
        this._treeComponent.on("UserSelectedPaths", (this._onTreeUserSelectedEditName = this._onTreeUserSelectedImages.bind(this)));
        this._treeComponent.on("UserSelectedPathForEditName", (this._onUserDoubleClick = this._onUserDoubleClick.bind(this)));
        this._addButton.on("UserClick", (this._onUserAddButtonClick = this._onUserAddButtonClick.bind(this)));
        this._removeButton.on("UserClick", (this._onUserRemoveButtonClick = this._onUserRemoveButtonClick.bind(this)));
        this._removeAllButton.on("UserClick", (this._onUserRemoveAllButtonClick = this._onUserRemoveAllButtonClick.bind(this)));

        /* Sorting eveents */
        this._selectedImagesSection.on("Value", (this._userValueSelected = this._userValueSelected.bind(this)));
        this._sortUp.addEventListener("click", (this._onUserSortUp = this._onUserSortUp.bind(this)));
        this._sortDown.addEventListener("click", (this._onUserSortDown = this._onUserSortDown.bind(this)));
    }

    _unregisterListeners() {
        this._treeComponent.removeListener("UserSelectedPath", this._onTreeUserOpenPath);
        this._treeComponent.removeListener("UserSelectedPaths", this._onTreeUserSelectedImages);
        this._treeComponent.removeListener("UserSelectedPathForEditName", this._onUserDoubleClick);
        this._addButton.removeListener("UserClick", this._onUserAddButtonClick);
        this._removeButton.removeListener("UserClick", this._onUserRemoveButtonClick);
        this._removeAllButton.removeListener("UserClick", this._onUserRemoveAllButtonClick);
        this._selectedImagesSection.removeListener("Value", this._userValueSelected);
        this._sortUp.removeEventListener("click", this._onUserSortUp);
        this._sortDown.removeEventListener("click", this._onUserSortDown);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    _userValueSelected(selectedAll) {
        if (selectedAll.length === 1) {
            this._sortContainer.removeAttribute("disabled");
        } else {
            this._sortContainer.setAttribute("disabled", "disabled");
        }
    }

    _onUserSortUp() {
        if (_.isEmpty(this._sortUp.getAttribute("disabled"))) {
            let selected = this._selectedImagesSection._lastSelected;
            if (_.isObject(this._selectedImagesSection._lastSelected)) {
                let currentValue = _.cloneDeep(this._selectedImagesSection.options);
                let searchValue = selected;
                currentValue.forEach((value, index) => {
                    if (_.isEqual(value.label, searchValue.name) && index > 0) {
                        currentValue.splice(index, 1);
                        currentValue.splice(index - 1, 0, value);
                    }
                });
                this._selectedImagesSection.options = currentValue;
                this._selectedImagesSection.setSelectedOption([_.find(currentValue, { label: selected.name }).value]);

                this._updateAssociationsOnSort();
            }
        }
    }

    _onUserSortDown() {
        if (_.isEmpty(this._sortUp.getAttribute("disabled"))) {
            let selected = this._selectedImagesSection._lastSelected;
            if (_.isObject(this._selectedImagesSection._lastSelected)) {
                let currentValue = _.cloneDeep(this._selectedImagesSection.options);
                let searchValue = selected;
                let applyMove = true;
                currentValue.forEach((value, index) => {
                    if (applyMove && _.isEqual(value.label, searchValue.name) && index < currentValue.length - 1) {
                        currentValue.splice(index, 1);
                        currentValue.splice(index + 1, 0, value);
                        applyMove = false;
                    }
                });
                this._selectedImagesSection.options = currentValue;
                this._selectedImagesSection.setSelectedOption([_.find(currentValue, { label: selected.name }).value]);

                this._updateAssociationsOnSort();
            }
        }
    }

    _updateAssociationsOnSort() {
        const imageIds = _.get(this._selectedImagesSection, "options", []).map((item) => {
            return { id: item.value.id };
        });

        this._updateRelatedImages(imageIds).then(() => {
            this._updateGenericPatches();
        });
    }

    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                return Promise.all([this._renderImagesTreeView(), this._renderSelControls(), this._renderSelSelectbox()]);
            })
            .then(() => {
                this._imagesContainer.append(
                    this._treeComponent.renderer.rootElement,
                    this._selControlContainer,
                    this._selectedImagesSection.renderer.rootElement
                );
                this._imagesContainer.appendChild(this._sortContainer);
                this._formFieldset.appendChild(this._legend);
                this._formFieldset.appendChild(this._imagesContainer);
                this._rootElem.appendChild(this._formFieldset);
                this._registerListeners();
            });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    shutdown() {
        this._unregisterListeners();
        let shutdowns = [
            this._addButton.shutdown(),
            this._removeButton.shutdown(),
            this._removeAllButton.shutdown(),
            this._selectedImagesSection.shutdown(),
            this._treeComponent.shutdown()
        ];
        return Promise.all(shutdowns)
            .then(() => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._addButton = undefined;
                this._removeButton = undefined;
                this._removeAllButton = undefined;
                this._selectedImagesSection = undefined;
                this._selControlContainer = undefined;
                this._treeComponent = undefined;
                this._formFieldset = undefined;
                this._imagesContainer = undefined;
                this._legend = undefined;
                this._addButtonIcon = undefined;
                this._removeButtonIcon = undefined;
                this._removeAllButtonIcon = undefined;
                return Promise.resolve();
            })
            .catch((err) => {
                console.error(err);
            });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    _renderImagesTreeView() {
        return this._model.getImagesForPath().then((paths) => {
            const imageData = paths
                .filter((currentImage) => {
                    return !this.model._selectedImages.some((selectedImage) => {
                        return currentImage.id === selectedImage.id;
                    });
                })
                .map((imageEntity) => {
                    let image = this.model._getConvertedImageDataFromFeed(imageEntity, false);
                    return image;
                });
            // this._addImagesToPathNode(imageData);

            const pathsImages = [].concat(_.sortBy(this._model._pathsData, "name"), _.sortBy(imageData, "name"));

            this._treeComponent = new TreeComponent({
                autoOpen: false,
                pageFeed: this._model.groupTree(pathsImages),
                simple: true
            });
            this._rootElem.appendChild(this._treeComponent.renderer.rootElement);
            this._disableAddAction();
            return Promise.resolve(this._treeComponent);
        });
    }

    _renderSelControls() {
        this._selControlContainer = document.createElement("sel-control-container");
        this._disableAddAction();
        return Promise.resolve().then(() => {
            this._selControlContainer.appendChild(this._addButton.renderer.rootElement);
            this._selControlContainer.appendChild(this._removeButton.renderer.rootElement);
            this._selControlContainer.appendChild(this._removeAllButton.renderer.rootElement);
            this._addButton.renderer.rootElement.classList.add("target-button");
            this._removeButton.renderer.rootElement.classList.add("target-button");
            this._removeAllButton.renderer.rootElement.classList.add("target-button");
        });
    }

    _renderSelSelectbox() {
        if (!_.isEmpty(this._model._imageDetailsView._patchesCreated)) {
            this._addImagesFromDirtyState();
        }

        this._applySorting();

        if (this.model._selectedImages.length > 0) {
            this._selectedImagesSection.options = this._convertSelectListCompatibleImages();
        } else {
            this._disableRemoveAction();
        }
        this._selectedImagesSection.renderer.render();
    }

    _applySorting() {
        if (!_.isEmpty(this._model._imageDetailsView._imageSortOrder)) {
            this.model._selectedImages.sort((a, b) => {
                return (
                    this._model._imageDetailsView._imageSortOrder.indexOf(_.get(a, "id")) -
                    this._model._imageDetailsView._imageSortOrder.indexOf(_.get(b, "id"))
                );
            });
        } else if (!_.isEmpty(_.get(this._model._osPlan, "imageAssociations." + this._model._key + ".imageIds", []))) {
            const imagesFromModel = _.get(this._model._osPlan, "imageAssociations." + this._model._key + ".imageIds", []);
            this.model._selectedImages.sort((a, b) => {
                return imagesFromModel.indexOf(_.get(a, "id")) - imagesFromModel.indexOf(_.get(b, "id"));
            });
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _addImagesFromDirtyState() {
        _.forEach(this._model._imageDetailsView._patchesCreated, (item) => {
            if (item.op === "add" || item.op === "replace") {
                if (_.isEmpty(this._model._selectedImages.filter((item1) => item1.id === item.id))) {
                    this._model._selectedImages.push({
                        name: item.name,
                        id: item.id,
                        parentId: ""
                    });
                }
            } else {
                this._model._selectedImages = this._model._selectedImages.filter((selectedItem) => item.id !== selectedItem.id);
            }
        });
    }

    _onTreeUserOpenPath(pathNode) {
        // if (
        //     !pathNode.state.open &&
        //     pathNode.type !== "image" &&
        //     !_.find(pathNode.children, ["type", "image"])
        // ) {
        //     this._disableAddAction();
        //     this._model.getImagesForPath(pathNode).then((paths) => {
        //         const imageData = paths
        //             .filter((currentImage) => {
        //                 return !this.model._selectedImages.some((selectedImage) => {
        //                     return currentImage.id === selectedImage.id;
        //                 });
        //             })
        //             .map((imageEntity) => {
        //                 let image = this.model._getConvertedImageDataFromFeed(imageEntity, false);
        //                 return image;
        //             });
        //         this._addImagesToPathNode(imageData);
        //     });
        // } else
        this._disableAddAction();

        if (pathNode.type === "image") {
            this._enableAddAction();
            this.model._currentSelection = [pathNode];
        }
    }

    _onTreeUserSelectedImages(paths) {
        this.model._currentSelection = paths.length > 1 && _.filter(paths, ["type", "image"]);
    }

    _onUserAddButtonClick(/*e*/) {
        this._updateSelectedImagesSection();
        let imagesToUpdate = this.model._selectedImages.map((image) => {
            return { id: image.id, name: image.name };
        });
        this._updateRelatedImages(imagesToUpdate).then(() => {
            this._updateGenericPatches();
        });
        this._enableRemoveAction();
    }

    _onUserDoubleClick(pathNode) {
        if (pathNode.type === "image") {
            this.model._currentSelection = [pathNode];
            this._onUserAddButtonClick();
        }
    }

    _onUserRemoveButtonClick() {
        let selectedValues = this._selectedImagesSection.getValue();
        // remove selectedValues from the options and re render select box.

        let remainingImages = this._selectedImagesSection.options.filter((option) => {
            return !selectedValues.some((val) => {
                return option.value.id === val.id;
            });
        });

        let deletedImages = this._selectedImagesSection.options.filter((option) => {
            return selectedValues.some((val) => {
                return option.value.id === val.id;
            });
        });
        let deletedImagesArray = [];
        deletedImages.forEach((item) => {
            deletedImagesArray.push(item.value.id);
        });

        this._selectedImagesSection.options = remainingImages;
        this._selectedImagesSection.renderer.render();

        // find the selected values from this.model._selectedImages. Remove them from here.
        let imagesData = [];
        let images = [];
        let result = this.model._selectedImages.filter((option) => {
            return !selectedValues.some((val) => {
                if (option.id === val.id) {
                    let deselectedConvertedImages = this.model._getConvertedImageDataFromSelectedImages(option);
                    imagesData.push(deselectedConvertedImages);
                } else {
                    images.push(val.id);
                }
                return option.id === val.id;
            });
        });
        this.model._selectedImages = result;
        this._removeRelatedImages(deletedImagesArray).then(() => {
            this._updateGenericPatches();
        });
        this._addImagesToPathNode(imagesData);

        if (this.model._selectedImages.length === 0) {
            this._disableRemoveAction();
        }
    }

    _updateGenericPatches() {
        if (!_.isEmpty(this._selectedImagesSection.options)) {
            this._model._imageDetailsView._imageSortOrder = this._selectedImagesSection.options.map((item) => item.value.id);
        } else {
            this._model._imageDetailsView._imageSortOrder = [];
        }

        let oldPatches = this._model._imageDetailsView._patchesCreated;
        this._model._imageDetailsView._patchesCreated = [];
        let currentImages = _.isEmpty(_.get(this._model._osPlan.getPristineProperties().imageAssociations, this._model._key, []))
            ? []
            : _.get(this._model._osPlan.getPristineProperties().imageAssociations, this._model._key, []).imageIds;

        let selectList = [];
        if (!_.isEmpty(this._selectedImagesSection.options)) {
            selectList = this._selectedImagesSection.options.map((item) => item.value.id);
        }

        let imagesAdded = _.differenceWith(selectList, currentImages);

        let imagesRemoved = _.differenceWith(currentImages, selectList);

        if (!_.isEmpty(imagesAdded)) {
            imagesAdded.map((item) => {
                if (_.isEmpty(this._model._imageDetailsView._patchesCreated.filter((currentItem) => currentItem.id === item.id))) {
                    this._model._imageDetailsView._patchesCreated.push({
                        id: item,
                        name: this._getImageNameFromId(item, oldPatches),
                        op: "add"
                    });
                }
            });
        }
        if (!_.isEmpty(imagesRemoved)) {
            imagesRemoved.map((item) => {
                if (_.isEmpty(this._model._imageDetailsView._patchesCreated.filter((currentItem) => currentItem.id === item.id))) {
                    this._model._imageDetailsView._patchesCreated.push({
                        id: item,
                        name: this._getImageNameFromId(item, oldPatches),
                        op: "remove"
                    });
                }
            });
        }
    }

    _getImageNameFromId(imageId, oldPatches) {
        if (
            !_.isEmpty(
                _.get(
                    _.get(
                        oldPatches.filter((item) => item.id === imageId),
                        "0",
                        []
                    ),
                    "id",
                    []
                )
            )
        ) {
            return _.get(
                _.get(
                    oldPatches.filter((item) => item.id === imageId),
                    "0",
                    []
                ),
                "name",
                []
            );
        } else if (!_.isEmpty(this.model._selectedImagesFeed) && !_.isEmpty(this.model._selectedImagesFeed.filter((item) => item.id === imageId))) {
            return _.get(
                _.find(
                    this.model._selectedImagesFeed.filter((item) => item.id === imageId),
                    {
                        id: imageId
                    }
                ),
                "_pristineProperties.fileName",
                []
            );
        }

        const imageNode = this.model._selectedImages.map((item) => item);
        let nodeName = "";
        _.forEach(imageNode, (node) => {
            if (node.id === imageId) {
                nodeName = node.name;
                return false;
            }
        });

        return nodeName;
    }

    _onUserRemoveAllButtonClick() {
        let imagesData = [];
        let imagesToRemove = [];
        this.model._selectedImages.map((image) => {
            let convertedImage = this.model._getConvertedImageDataFromSelectedImages(image);
            imagesData.push(convertedImage);
            imagesToRemove.push(image.id);
        });
        this._removeRelatedImages(imagesToRemove).then(() => {
            this._updateGenericPatches();
        });

        this._selectedImagesSection.options = [];
        this._selectedImagesSection.renderer.render();
        this._addImagesToPathNode(imagesData);

        this.model._selectedImages = [];
        this._disableRemoveAction();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE ACTION METHODS */
    /*******************************************/

    _disableAddAction() {
        this._addButton.disabled = true;
    }

    _enableAddAction() {
        this._addButton.disabled = false;
    }

    _disableRemoveAction() {
        this._removeButton.disabled = true;
        this._removeAllButton.disabled = true;
    }

    _enableRemoveAction() {
        this._removeButton.disabled = false;
        this._removeAllButton.disabled = false;
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _removeSelectedItemsFromTree(node) {
        Promise.resolve(this._treeComponent.removeNode(node));
    }

    _updateSelectedImagesSection() {
        this.model._currentSelection.map((item) => {
            let selectedImages = this.model._getConvertedImageDataFromSelectedImages(item);
            this.model._selectedImages.push(selectedImages);
            this._removeSelectedItemsFromTree(item);
        });
        let selectedImagesOptions = this._convertSelectListCompatibleImages();
        this._selectedImagesSection.options = selectedImagesOptions;
        this._selectedImagesSection.renderer.render();
    }

    _convertSelectListCompatibleImages() {
        let convertedImagesList = this.model._selectedImages.map((node) => {
            return {
                label: node.name,
                value: {
                    id: node.id,
                    name: node.name,
                    parentId: node.parentId,
                    type: "image",
                    fileType: node.fileType,
                    fileSize: node.fileSize,
                    imageType: node.imageType
                }
            };
        });
        return convertedImagesList;
    }

    _addImagesToPathNode(imageData) {
        Promise.resolve(this._treeComponent.updateNode(imageData));
    }

    _updateRelatedImages(imageIds) {
        let protocol = this.model._imageDetailsView.renderer._transferProtocol.getValue(),
            windowSize = this.model._imageDetailsView.renderer._windowSize.getValue();

        return this.model._osPlan.associateImagesToKey(
            this.model._key,
            protocol,
            imageIds.map((item) => item.id),
            windowSize
        );
    }

    _removeRelatedImages(imagesToRemove) {
        return this.model._osPlan.removeImagesForAssociationKey(this.model._key, imagesToRemove);
    }

    _clearAllAssociationsForKey() {
        return this.model._osPlan.clearImageAssociationKey(this.model._key);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = OsPlanServerImagesRenderer;
